// MedicationConsentSheet — parent-only form to add or edit a medication
// authorization. Withdrawal is a separate one-tap action surfaced in
// the parent list view (not inside this sheet).

import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { X } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  createAuthorization,
  MED_ROUTES,
  updateAuthorization,
} from "@/lib/medications";

export default function MedicationConsentSheet({
  open,
  onOpenChange,
  child,           // {id, family_id, organization_id, first_name, last_name}
  existing,        // null = create; row = edit
  authorizedBy,    // profileId
  onSaved,
}) {
  const isEdit = !!existing?.id;
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setForm({
        medication_name: existing.medication_name || "",
        dosage: existing.dosage || "",
        route: existing.route || "oral",
        schedule: existing.schedule || "",
        reason: existing.reason || "",
        warnings: existing.warnings || "",
        starts_on: existing.starts_on || todayStr(),
        ends_on: existing.ends_on || "",
      });
    } else {
      setForm(blank());
    }
  }, [open, existing]);

  const onSubmit = async () => {
    if (!form.medication_name.trim()) {
      toast.error("Medication name is required");
      return;
    }
    if (!form.dosage.trim()) {
      toast.error("Dosage is required");
      return;
    }
    if (form.ends_on && form.ends_on < form.starts_on) {
      toast.error("End date must be on or after start date");
      return;
    }
    setBusy(true);
    try {
      if (isEdit) {
        const row = await updateAuthorization(existing.id, {
          medication_name: form.medication_name.trim(),
          dosage: form.dosage.trim(),
          route: form.route,
          schedule: form.schedule?.trim() || null,
          reason: form.reason?.trim() || null,
          warnings: form.warnings?.trim() || null,
          starts_on: form.starts_on,
          ends_on: form.ends_on || null,
        });
        toast.success(`Updated ${row.medication_name}`);
        onSaved?.(row);
      } else {
        const row = await createAuthorization({
          organizationId: child.organization_id,
          childId: child.id,
          familyId: child.family_id,
          medicationName: form.medication_name,
          dosage: form.dosage,
          route: form.route,
          schedule: form.schedule,
          reason: form.reason,
          warnings: form.warnings,
          startsOn: form.starts_on,
          endsOn: form.ends_on || null,
          authorizedBy,
        });
        toast.success(`Authorised ${row.medication_name}`);
        onSaved?.(row);
      }
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="medication-consent-sheet"
        side="bottom"
        className="max-h-[92dvh] overflow-y-auto rounded-t-3xl border-t border-neutral-200 sm:max-w-md sm:mx-auto"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading text-xl">
            {isEdit ? "Edit medication consent" : "Authorise a medication"}
          </SheetTitle>
          <SheetDescription>
            For{" "}
            <strong className="text-neutral-900">
              {[child?.first_name, child?.last_name].filter(Boolean).join(" ")}
            </strong>
            . Teachers will see this consent and may administer doses
            within these instructions.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-5 grid grid-cols-1 gap-3">
          <Field label="Medication" required>
            <input
              type="text"
              data-testid="med-name"
              value={form.medication_name}
              onChange={(e) => setForm((f) => ({ ...f, medication_name: e.target.value }))}
              placeholder="e.g. Children's Tylenol"
              className={INPUT_CLS}
              autoComplete="off"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Dosage" required>
              <input
                type="text"
                data-testid="med-dosage"
                value={form.dosage}
                onChange={(e) => setForm((f) => ({ ...f, dosage: e.target.value }))}
                placeholder="5 ml"
                className={INPUT_CLS}
                autoComplete="off"
              />
            </Field>
            <Field label="Route">
              <select
                data-testid="med-route"
                value={form.route}
                onChange={(e) => setForm((f) => ({ ...f, route: e.target.value }))}
                className={INPUT_CLS}
              >
                {MED_ROUTES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Schedule / instructions">
            <input
              type="text"
              data-testid="med-schedule"
              value={form.schedule}
              onChange={(e) => setForm((f) => ({ ...f, schedule: e.target.value }))}
              placeholder="every 4 hours as needed for fever"
              className={INPUT_CLS}
            />
          </Field>

          <Field label="Reason">
            <input
              type="text"
              data-testid="med-reason"
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              placeholder="seasonal allergies"
              className={INPUT_CLS}
            />
          </Field>

          <Field
            label="Warnings"
            help="Anything teachers should be aware of when giving this dose."
          >
            <textarea
              rows={2}
              data-testid="med-warnings"
              value={form.warnings}
              onChange={(e) => setForm((f) => ({ ...f, warnings: e.target.value }))}
              placeholder="may cause drowsiness; do not exceed 4 doses in 24h"
              className={INPUT_CLS}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Starts on">
              <input
                type="date"
                data-testid="med-starts-on"
                value={form.starts_on}
                onChange={(e) => setForm((f) => ({ ...f, starts_on: e.target.value }))}
                className={INPUT_CLS}
              />
            </Field>
            <Field label="Ends on" help="Leave empty for ongoing.">
              <input
                type="date"
                data-testid="med-ends-on"
                value={form.ends_on}
                onChange={(e) => setForm((f) => ({ ...f, ends_on: e.target.value }))}
                min={form.starts_on || undefined}
                className={INPUT_CLS}
              />
            </Field>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            data-testid="med-cancel"
            onClick={() => onOpenChange?.(false)}
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          >
            <X size={12} weight="bold" />
            Cancel
          </button>
          <button
            type="button"
            data-testid="med-save"
            disabled={busy}
            onClick={onSubmit}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
          >
            {busy ? "Saving…" : isEdit ? "Save changes" : "Authorise"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

const INPUT_CLS =
  "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

function Field({ label, required, help, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">
        {label}
        {required && <span className="ml-0.5 text-[#C6851E]">*</span>}
      </span>
      {children}
      {help && <p className="mt-1 text-[11px] text-neutral-500">{help}</p>}
    </label>
  );
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function blank() {
  return {
    medication_name: "",
    dosage: "",
    route: "oral",
    schedule: "",
    reason: "",
    warnings: "",
    starts_on: todayStr(),
    ends_on: "",
  };
}
